import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Bug, 
  Lightbulb, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Trash2,
  RefreshCw,
  User,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackManagementPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('all');
  const [filtroStato, setFiltroStato] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [noteAdmin, setNoteAdmin] = useState('');
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    // Solo admin può accedere
    if (user?.ruolo !== 'admin') {
      toast.error('Accesso non autorizzato');
      navigate('/dashboard');
      return;
    }
    loadFeedback();
  }, [user, navigate]);

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroTipo !== 'all') params.append('tipo', filtroTipo);
      if (filtroStato !== 'all') params.append('stato', filtroStato);
      
      const response = await axios.get(`${API}/feedback?${params.toString()}`, getAuthHeaders());
      setFeedback(response.data.feedback || []);
      setStats(response.data.statistiche);
    } catch (error) {
      console.error('Errore caricamento feedback:', error);
      toast.error('Errore nel caricamento dei feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.ruolo === 'admin') {
      loadFeedback();
    }
  }, [filtroTipo, filtroStato]);

  const updateFeedback = async (id, updates) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/feedback/${id}`, updates, getAuthHeaders());
      toast.success('Feedback aggiornato');
      loadFeedback();
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error('Errore aggiornamento:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Eliminare questo feedback?')) return;
    
    try {
      await axios.delete(`${API}/feedback/${id}`, getAuthHeaders());
      toast.success('Feedback eliminato');
      loadFeedback();
      setSelectedFeedback(null);
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getStatoBadge = (stato, risolto) => {
    if (risolto) return <Badge className="bg-green-100 text-green-700">Risolto</Badge>;
    switch (stato) {
      case 'nuovo': return <Badge className="bg-blue-100 text-blue-700">Nuovo</Badge>;
      case 'in_lavorazione': return <Badge className="bg-amber-100 text-amber-700">In Lavorazione</Badge>;
      case 'risolto': return <Badge className="bg-green-100 text-green-700">Risolto</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-700">{stato}</Badge>;
    }
  };

  const getTipoBadge = (tipo) => {
    if (tipo === 'bug') {
      return <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><Bug className="w-3 h-3" /> Bug</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Miglioramento</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Gestione Feedback</h1>
                <p className="text-slate-500 text-sm">Bug e suggerimenti degli utenti</p>
              </div>
            </div>
            <Button variant="outline" onClick={loadFeedback} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-md bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-red-100 text-sm">Bug Totali</p>
                    <p className="text-xl sm:text-3xl font-bold">{stats.totale_bug}</p>
                  </div>
                  <Bug className="w-10 h-10 text-red-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-emerald-100 text-sm">Miglioramenti</p>
                    <p className="text-xl sm:text-3xl font-bold">{stats.totale_miglioramenti}</p>
                  </div>
                  <Lightbulb className="w-10 h-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-blue-100 text-sm">Nuovi</p>
                    <p className="text-xl sm:text-3xl font-bold">{stats.nuovi}</p>
                  </div>
                  <Clock className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-green-100 text-sm">Risolti</p>
                    <p className="text-xl sm:text-3xl font-bold">{stats.risolti}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-md mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="miglioramento">Miglioramenti</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filtroStato} onValueChange={setFiltroStato}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="nuovo">Nuovi</SelectItem>
                  <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                  <SelectItem value="risolto">Risolti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feedback List */}
          <Card className="border-0 shadow-md lg:col-span-1 max-h-[600px] overflow-hidden flex flex-col">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="text-lg">
                Feedback ({feedback.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-slate-400">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                  Caricamento...
                </div>
              ) : feedback.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  Nessun feedback trovato
                </div>
              ) : (
                <div className="divide-y">
                  {feedback.map((f) => (
                    <div
                      key={f.id}
                      onClick={() => {
                        setSelectedFeedback(f);
                        setNoteAdmin(f.note_admin || '');
                      }}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedFeedback?.id === f.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        {getTipoBadge(f.tipo)}
                        {getStatoBadge(f.stato, f.risolto)}
                      </div>
                      <p className="text-sm text-slate-700 line-clamp-2 mb-2">{f.descrizione}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <User className="w-3 h-3" />
                        {f.utente_nome}
                        <Calendar className="w-3 h-3 ml-2" />
                        {new Date(f.created_at).toLocaleDateString('it-IT')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedback Detail */}
          <Card className="border-0 shadow-md lg:col-span-2">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="text-lg">Dettaglio Feedback</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {selectedFeedback ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getTipoBadge(selectedFeedback.tipo)}
                      {getStatoBadge(selectedFeedback.stato, selectedFeedback.risolto)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteFeedback(selectedFeedback.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* User Info */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Utente:</span>
                        <p className="font-medium">{selectedFeedback.utente_nome}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Email:</span>
                        <p className="font-medium">{selectedFeedback.utente_email}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Ruolo:</span>
                        <p className="font-medium capitalize">{selectedFeedback.utente_ruolo}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Data:</span>
                        <p className="font-medium">
                          {new Date(selectedFeedback.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Descrizione</label>
                    <div className="p-4 bg-white border rounded-lg text-slate-700">
                      {selectedFeedback.descrizione}
                    </div>
                  </div>

                  {/* Admin Actions */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-slate-800 mb-4">Azioni Admin</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Stato</label>
                        <Select
                          value={selectedFeedback.stato}
                          onValueChange={(value) => updateFeedback(selectedFeedback.id, { stato: value })}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nuovo">Nuovo</SelectItem>
                            <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                            <SelectItem value="risolto">Risolto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Note Admin</label>
                        <Textarea
                          value={noteAdmin}
                          onChange={(e) => setNoteAdmin(e.target.value)}
                          placeholder="Aggiungi note interne..."
                          rows={3}
                        />
                        <Button
                          className="mt-2"
                          size="sm"
                          onClick={() => updateFeedback(selectedFeedback.id, { note_admin: noteAdmin })}
                          disabled={saving}
                        >
                          Salva Note
                        </Button>
                      </div>

                      {!selectedFeedback.risolto && (
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => updateFeedback(selectedFeedback.id, { risolto: true })}
                          disabled={saving}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Segna come Risolto
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Resolution Info */}
                  {selectedFeedback.risolto && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-700 font-medium flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Risolto da {selectedFeedback.risolto_da || 'Admin'}
                      </p>
                      {selectedFeedback.risolto_at && (
                        <p className="text-green-600 text-sm mt-1">
                          il {new Date(selectedFeedback.risolto_at).toLocaleString('it-IT')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Seleziona un feedback dalla lista per visualizzare i dettagli</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagementPage;
